(function () {
  const MOBILE_MEDIA_QUERY = '(max-width: 991px)';

  if (customElements.get('product-card-gallery')) return;

  customElements.define('product-card-gallery', class extends HTMLElement {
    constructor() {
      super();

      this.activeIndex = null;
      this.initialized = false;
      this.initFrame = null;
      this.scrollFrame = null;
      this.scrollResetTimer = null;

      this.handleInteractionMove = this.handleInteractionMove.bind(this);
      this.handleInteractionStart = this.handleInteractionStart.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handlePaginationClick = this.handlePaginationClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleSwatchChange = this.handleSwatchChange.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      window.cancelAnimationFrame(this.initFrame);
      this.initFrame = window.requestAnimationFrame(() => {
        this.initFrame = null;
        this.initialize();
      });
    }

    initialize() {
      if (!this.isConnected || this.initialized) return;

      this.track = this.querySelector('[data-product-card-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('[data-product-card-gallery-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-product-card-gallery-dot]'));
      this.previousButton = this.querySelector('[data-product-card-gallery-previous]');
      this.nextButton = this.querySelector('[data-product-card-gallery-next]');
      this.counter = this.querySelector('[data-product-card-gallery-counter]');
      this.pagination = this.querySelector('[data-product-card-gallery-pagination]');
      this.card = this.closest('.\\#product-card');
      this.swatchInputs = Array.from(this.card?.querySelectorAll('.\\#product-card-swatch-input') || []);

      if (!this.track || this.slides.length < 2) return;

      this.initialized = true;
      this.mobileMedia = window.matchMedia(MOBILE_MEDIA_QUERY);
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      this.track.addEventListener('touchstart', this.handleInteractionStart, { passive: true });
      this.track.addEventListener('touchmove', this.handleInteractionMove, { passive: true });
      this.track.addEventListener('pointerdown', this.handleInteractionStart, { passive: true });
      this.track.addEventListener('pointermove', this.handleInteractionMove, { passive: true });
      this.pagination?.addEventListener('click', this.handlePaginationClick);
      this.swatchInputs.forEach(input => input.addEventListener('click', this.handleSwatchChange));

      if (this.mobileMedia.addEventListener) {
        this.mobileMedia.addEventListener('change', this.handleMediaChange);
      } else {
        this.mobileMedia.addListener(this.handleMediaChange);
      }

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.updateFromScrollPosition());
        this.resizeObserver.observe(this.track);
      }

      this.syncDotTabStops();
      this.setActiveIndex(this.getActiveIndex());
    }

    disconnectedCallback() {
      window.cancelAnimationFrame(this.initFrame);
      this.initFrame = null;
      if (!this.initialized) return;

      this.track.removeEventListener('scroll', this.handleScroll);
      this.track.removeEventListener('touchstart', this.handleInteractionStart);
      this.track.removeEventListener('touchmove', this.handleInteractionMove);
      this.track.removeEventListener('pointerdown', this.handleInteractionStart);
      this.track.removeEventListener('pointermove', this.handleInteractionMove);
      this.pagination?.removeEventListener('click', this.handlePaginationClick);
      this.swatchInputs.forEach(input => input.removeEventListener('click', this.handleSwatchChange));

      if (this.mobileMedia.removeEventListener) {
        this.mobileMedia.removeEventListener('change', this.handleMediaChange);
      } else {
        this.mobileMedia.removeListener(this.handleMediaChange);
      }

      this.resizeObserver?.disconnect();
      window.cancelAnimationFrame(this.scrollFrame);
      window.clearTimeout(this.scrollResetTimer);
      this.initialized = false;
    }

    handleInteractionStart(event) {
      if (!this.mobileMedia.matches || event.pointerType === 'mouse') return;

      const point = this.getInteractionPoint(event);
      if (!point) return;

      this.galleryConsumesGesture = null;
      this.interactionStartX = point.x;
      this.interactionStartY = point.y;
      this.interactionStartScrollLeft = this.track.scrollLeft;
    }

    handleInteractionMove(event) {
      if (!this.mobileMedia.matches || event.pointerType === 'mouse') return;

      const point = this.getInteractionPoint(event);
      if (!point || !Number.isFinite(this.interactionStartX)) return;

      const deltaX = point.x - this.interactionStartX;
      const deltaY = point.y - this.interactionStartY;
      const horizontalDistance = Math.abs(deltaX);

      if (horizontalDistance <= 6 || horizontalDistance <= Math.abs(deltaY)) return;

      if (this.galleryConsumesGesture === null) {
        const maxScrollLeft = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const startedAtBeginning = this.interactionStartScrollLeft <= 1;
        const startedAtEnd = this.interactionStartScrollLeft >= maxScrollLeft - 1;
        const movingToPreviousImage = deltaX > 0;

        this.galleryConsumesGesture = movingToPreviousImage
          ? !startedAtBeginning
          : !startedAtEnd;
      }

      if (this.galleryConsumesGesture) {
        // Keep the gesture in the image gallery while another image is
        // available; at either edge, let the surrounding carousel take over.
        event.stopPropagation();
      }
    }

    getInteractionPoint(event) {
      const point = event.touches?.[0] || event;
      if (!Number.isFinite(point.clientX) || !Number.isFinite(point.clientY)) return null;

      return { x: point.clientX, y: point.clientY };
    }

    handleScroll() {
      if (!this.mobileMedia.matches) return;

      this.setAttribute('data-gallery-scrolling', '');

      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = window.requestAnimationFrame(() => this.updateFromScrollPosition());

      window.clearTimeout(this.scrollResetTimer);
      this.scrollResetTimer = window.setTimeout(() => {
        this.removeAttribute('data-gallery-scrolling');
      }, 180);
    }

    handleSwatchChange() {
      if (!this.mobileMedia.matches) return;

      window.clearTimeout(this.scrollResetTimer);
      this.track.scrollTo({ behavior: 'auto', left: 0 });
      this.removeAttribute('data-gallery-scrolling');
      this.setActiveIndex(0);
    }

    handlePaginationClick(event) {
      const dot = event.target.closest('[data-product-card-gallery-dot]');
      const previousButton = event.target.closest('[data-product-card-gallery-previous]');
      const nextButton = event.target.closest('[data-product-card-gallery-next]');
      if ((!dot && !previousButton && !nextButton) || !this.mobileMedia.matches) return;

      let index = this.activeIndex ?? this.getActiveIndex();
      if (dot) {
        index = this.dots.indexOf(dot);
      } else if (previousButton) {
        index -= 1;
      } else {
        index += 1;
      }

      const slide = this.slides[index];
      if (!slide) return;

      event.preventDefault();
      event.stopPropagation();
      this.setAttribute('data-gallery-scrolling', '');
      window.clearTimeout(this.scrollResetTimer);
      this.scrollResetTimer = window.setTimeout(() => {
        this.removeAttribute('data-gallery-scrolling');
      }, 180);
      this.setActiveIndex(index);

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({
        behavior: reduceMotion ? 'auto' : 'smooth',
        left: slide.offsetLeft
      });
    }

    handleMediaChange() {
      this.track.scrollTo({ behavior: 'auto', left: 0 });
      this.setActiveIndex(0);
      this.syncDotTabStops();
    }

    updateFromScrollPosition() {
      if (!this.mobileMedia.matches) return;
      this.setActiveIndex(this.getActiveIndex());
    }

    getActiveIndex() {
      const trackLeft = this.track.getBoundingClientRect().left;
      let closestDistance = Number.POSITIVE_INFINITY;
      let closestIndex = 0;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    syncDotTabStops() {
      const isMobile = this.mobileMedia.matches;
      this.dots.forEach(dot => { dot.tabIndex = isMobile ? 0 : -1; });
      [this.previousButton, this.nextButton].forEach(button => {
        if (button) button.tabIndex = isMobile && !button.disabled ? 0 : -1;
      });
    }

    setActiveIndex(index) {
      const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      if (nextIndex === this.activeIndex) return;

      this.activeIndex = nextIndex;
      this.dataset.activeIndex = String(nextIndex);

      if (this.previousButton) {
        this.previousButton.disabled = nextIndex === 0;
      }
      if (this.nextButton) {
        this.nextButton.disabled = nextIndex === this.slides.length - 1;
      }
      this.syncDotTabStops();

      this.dots.forEach((dot, dotIndex) => {
        if (dotIndex === nextIndex) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (this.counter) {
        const current = String(nextIndex + 1);
        const total = String(this.slides.length);
        const position = `${current} / ${total}`;
        this.counter.dataset.current = current;
        this.counter.dataset.total = total;
        this.counter.textContent = position;
        this.counter.setAttribute('aria-label', position);
      }
    }
  });
})();
